import React, { useState, useEffect } from 'react';

interface SvgIconProps {
  /** 图标名称（不包含.svg后缀） */
  name: string;
  /** 图标宽度 */
  width?: number | string;
  /** 图标高度 */
  height?: number | string;
  /** 图标颜色 */
  color?: string;
  /** 自定义类名 */
  className?: string;
  /** 点击事件处理函数 */
  onClick?: () => void;
}

/**
 * SVG图标组件
 * 使用SVGR动态导入SVG图标
 * 基于Rsbuild的SVGR插件实现
 * @see https://rsbuild.dev/zh/plugins/list/plugin-svgr
 */
const SvgIcon: React.FC<SvgIconProps> = ({
  name,
  width = 24,
  height = 24,
  color,
  className = '',
  onClick,
}) => {
  const [Icon, setIcon] = useState<React.ComponentType<React.SVGProps<SVGSVGElement>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const importIcon = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 使用?react查询参数动态导入SVG图标
        const module = await import(`../../assets/icons/${name}.svg?react`);
        setIcon(() => module.default);
      } catch (err) {
        console.error(`无法加载图标: ${name}.svg`, err);
        setError(err instanceof Error ? err : new Error('加载图标失败'));
      } finally {
        setLoading(false);
      }
    };

    importIcon();
  }, [name]);

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  if (loading) {
    return <span style={{ width, height, display: 'inline-block' }} />;
  }

  if (error || !Icon) {
    return (
      <span 
        style={{ 
          width, 
          height, 
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          borderRadius: '4px',
          color: '#666'
        }}
      >
        {name.charAt(0)}
      </span>
    );
  }

  return (
    <span
      className={`svg-icon ${className}`}
      style={{ 
        width, 
        height, 
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : "img"}
      aria-label={`${name} icon`}
      tabIndex={onClick ? 0 : undefined}
    >
      <Icon 
        width="100%" 
        height="100%" 
        style={{ fill: color }} 
      />
    </span>
  );
};

export default SvgIcon;

/**
 * 使用示例
 * <SvgIcon name="icon-name" width={24} height={24} color="#000000" />
 * 基本用法
 * <SvgIcon name="home" />
 * 自定义大小
 * <SvgIcon name="user" width={32} height={32} />
 * 自定义颜色
 * <SvgIcon name="settings" color="#ff0000" />
 * 带点击事件
 * <SvgIcon name="notification" onClick={() => alert('点击了图标')} />
 * 自定义类名
 * <SvgIcon name="search" className="search-icon" />
 */
